import React, { useMemo } from 'react'
import { generatePath } from 'react-router-dom'
import { NavigationHeader } from '@/components/NavigationHeader/NavigationHeader'
import { Badge, CompanyTag } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Body1 } from '@/components/ui/typography'
import { LeadDetailsDto } from 'api/Leads/Types/LeadDetailsDto'
import { Link } from 'components/Link'
import { useTabNavigate } from 'hooks/useTabNavigate'
import { Users } from 'lucide-react'
import { getLeadExternalSystems } from 'utils/externalSystems'
import { links } from 'utils/featureToggles'
import { isNotEmpty } from 'utils/is-empty'

import { ExternalLink } from '../../Clients/Navigation/ExternalLink'
import { LeadTab } from '../Details/LeadSlug'

interface Props {
  data: LeadDetailsDto
  isEdit: boolean
  tab: LeadTab
}

export const LeadNavigation: React.FC<Props> = (props) => {
  const tabNavigate = useTabNavigate()
  const { data } = props

  const externalSystems = useMemo(() => getLeadExternalSystems(data), [data])
  const orderedExternalSystems = [...externalSystems].sort(
    (a, b) => a.externalSystem.id - b.externalSystem.id
  )

  return (
    <div>
      <NavigationHeader>
        <Body1 bold>{`${data.firstName} ${data.lastName}`}</Body1>
        <CompanyAndExternalSystems data={data} orderedExternalSystems={orderedExternalSystems} />
        <Body1 bold className='ml-auto'>
          Status: <Badge>{data.client ? 'Converted' : 'Not Converted'}</Badge>
        </Body1>
      </NavigationHeader>

      <Tabs value={props.tab} onValueChange={tabNavigate} className='mt-2'>
        <div className='w-full border-b'>
          <TabsList>
            <TabsTrigger value={LeadTab.DETAILS}>Details</TabsTrigger>
            <TabsTrigger value={LeadTab.ACTIVITIES}>Activities</TabsTrigger>
            <TabsTrigger value={LeadTab.DEMO_ACCOUNTS}>Demo Accounts</TabsTrigger>
            <TabsTrigger value={LeadTab.WELCOME_ACCOUNTS}>Welcome Accounts</TabsTrigger>
            <TabsTrigger value={LeadTab.NOTES}>Notes</TabsTrigger>
          </TabsList>
        </div>
      </Tabs>
    </div>
  )
}

const CompanyAndExternalSystems = ({
  data,
  orderedExternalSystems,
}: {
  data: LeadDetailsDto
  orderedExternalSystems: ReturnType<typeof getLeadExternalSystems>
}) => {
  return (
    <div className='flex items-center gap-2 pl-4'>
      <CompanyTag id={data.tickmillCompany.id}>{data.tickmillCompany.name}</CompanyTag>
      {isNotEmpty(orderedExternalSystems) &&
        orderedExternalSystems.map((system, index) => (
          <ExternalLink
            size={24}
            key={system.id || `${system.externalSystem.id}-${index}`}
            data={system}
          />
        ))}
      {data.client && (
        <Link
          to={{
            pathname: generatePath(links.recordsClients.detailsUrl as string, {
              id: data.client.id,
              slug: 'details',
            }),
          }}
        >
          <Users size={24} />
        </Link>
      )}
    </div>
  )
}
